import Flag from '@/components/Flag'
import DuoFunnelLayout from '@/components/v2/DuoFunnelLayout'
import DuoMascotPrompt from '@/components/v2/DuoMascotPrompt'
import DuoOptionRow from '@/components/v2/DuoOptionRow'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form } from 'react-router-dom'
import type { DuoAnswer, StepScreenProps } from './types'

function CheckMark() {
  return (
    <svg
      aria-hidden="true"
      className="animate-check-pop h-5 w-5 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * The same mascot prompt and option rows as the single-select screen, but
 * selections accumulate. Field name is the English title and each checked row
 * contributes its own value, matching the shared `MultipleChoiceQuestionPage`.
 */
export default function MultiSelectStep({
  answers,
  interpolation,
  step,
  subtitleKey,
  titleKey,
  totalSteps,
}: StepScreenProps & {
  answers: DuoAnswer[]
  subtitleKey?: string
  titleKey: string
}) {
  const [selectedValues, setSelectedValues] = useState<string[]>([])

  const { t } = useTranslation()

  const fieldName = t(titleKey, { lng: 'en', ...interpolation })

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    setSelectedValues((previous) =>
      event.target.checked
        ? [...previous, value]
        : previous.filter((selected) => selected !== value),
    )
  }

  return (
    <Form method="post">
      <DuoFunnelLayout
        footer={
          <button
            className="duo-button-primary"
            data-testid="button-continue"
            disabled={selectedValues.length === 0}
            type="submit"
          >
            {t('shared.continue')}
          </button>
        }
        step={step}
        totalSteps={totalSteps}
      >
        <DuoMascotPrompt
          subtitle={subtitleKey ? t(subtitleKey, interpolation) : undefined}
          text={t(titleKey, interpolation)}
        />

        <div className="mt-8 flex grow flex-col gap-3">
          {answers.map((answer) => {
            const value = answer.value
              ? t(answer.value)
              : t(answer.title, { lng: 'en', ...interpolation })
            const selected = selectedValues.includes(value)

            return (
              <DuoOptionRow
                inputType="checkbox"
                key={answer.title}
                leading={<Flag emoji={answer.emoji} />}
                name={fieldName}
                onChange={handleChange}
                selected={selected}
                subtitle={
                  answer.subtitle
                    ? t(answer.subtitle, interpolation)
                    : undefined
                }
                title={t(answer.title, interpolation)}
                trailing={selected ? <CheckMark /> : undefined}
                value={value}
              />
            )
          })}
        </div>
      </DuoFunnelLayout>
    </Form>
  )
}
